/**
 * Unified Floating Hub - Central communication hub
 * =================================================
 * A beautiful FAB menu that consolidates:
 * - Phone/Dialpad
 * - WhatsApp Business
 * - Internal Chat
 * - Afikim AI Assistant
 */

import React, { useState, useEffect, useRef } from 'react';

const UnifiedFloatingHub = ({
  onOpenPhone,
  onOpenWhatsApp,
  onOpenInternalChat,
  onOpenAI,
  onOpenWorkspace,
  workspaceEnabled = true,
  phoneEnabled = true,
  phoneConnected = false,
  hasActiveCall = false,
  chatEnabled = true,
  aiEnabled = true,
  unreadMessages = {
    phone: 0,
    whatsapp: 0,
    chat: 0,
    ai: 0,
    workspace: 0
  }
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const menuRef = useRef(null);

  // Calculate total unread
  const totalUnread = Object.values(unreadMessages).reduce((a, b) => a + b, 0);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Toggle menu with animation
  const toggleMenu = () => {
    if (isOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsAnimating(false);
      }, 200);
    } else {
      setIsOpen(true);
    }
  };

  // Handle item click
  const handleItemClick = (action) => {
    setIsOpen(false);
    action?.();
  };

  // Menu items configuration
  const menuItems = [
    {
      id: 'phone',
      label: 'טלפון',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
        </svg>
      ),
      color: '#1a1a1a',
      activeColor: '#4caf50',
      onClick: onOpenPhone,
      badge: unreadMessages.phone,
      isActive: hasActiveCall,
      statusDot: phoneConnected ? '#4caf50' : '#ff9800'
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
      color: '#25D366',
      onClick: onOpenWhatsApp,
      badge: unreadMessages.whatsapp
    },
    {
      id: 'chat',
      label: 'צ\'אט ארגוני',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"/>
        </svg>
      ),
      color: '#2196F3',
      onClick: onOpenInternalChat,
      badge: unreadMessages.chat
    },
    {
      id: 'ai',
      label: 'Afikim AI',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 10.12h-6.78l2.74-2.82c-2.73-2.7-7.15-2.8-9.88-.1-2.73 2.71-2.73 7.08 0 9.79s7.15 2.71 9.88 0C18.32 15.65 19 14.08 19 12.1h2c0 1.98-.88 4.55-2.64 6.29-3.51 3.48-9.21 3.48-12.72 0-3.5-3.47-3.53-9.11-.02-12.58s9.14-3.47 12.65 0L21 3v7.12zM12.5 8v4.25l3.5 2.08-.72 1.21L11 13V8h1.5z"/>
        </svg>
      ),
      color: '#9C27B0',
      onClick: onOpenAI,
      badge: unreadMessages.ai
    },
    {
      // Communication Workspace V2 — Phase 2 PR #1 (audit §13 step 11).
      // Single entry point per owner directive 2026-06-29:
      // "the page is accessed from the floating button, not from the admin Communication Center."
      id: 'workspace',
      label: 'מרכז תקשורת',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 5a2 2 0 0 1 2-2h4v18H5a2 2 0 0 1-2-2V5zm8-2h2v18h-2V3zm4 0h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4V3z"/>
        </svg>
      ),
      color: '#0176d3',
      onClick: onOpenWorkspace,
      badge: unreadMessages.workspace
    }
  ].filter(item => !(item.id === 'chat' && !chatEnabled))
   .filter(item => !(item.id === 'ai' && !aiEnabled))
   .filter(item => !(item.id === 'workspace' && !workspaceEnabled))
   .filter(item => !(item.id === 'phone' && !phoneEnabled));

  return (
    <div ref={menuRef} style={styles.container}>
      {/* Menu Items */}
      {isOpen && (
        <div style={{
          ...styles.menu,
          animation: isAnimating ? 'menuOut 0.2s ease' : 'menuIn 0.25s ease'
        }}>
          {menuItems.map((item, index) => (
            <button
              key={item.id}
              style={{
                ...styles.menuItem,
                animation: isAnimating
                  ? `itemOut 0.15s ease ${index * 0.03}s`
                  : `itemIn 0.2s ease ${index * 0.05}s backwards`
              }}
              onClick={() => handleItemClick(item.onClick)}
              title={item.label}
            >
              {/* Icon container */}
              <div style={{
                ...styles.menuItemIcon,
                background: item.isActive ? item.activeColor : item.color
              }}>
                {item.icon}
                {/* Status dot for phone */}
                {item.statusDot && (
                  <div style={{
                    ...styles.statusDot,
                    background: item.statusDot
                  }} />
                )}
              </div>

              {/* Label */}
              <span style={styles.menuItemLabel}>{item.label}</span>

              {/* Badge */}
              {item.badge > 0 && (
                <div style={{
                  ...styles.badge,
                  background: item.id === 'whatsapp' ? '#25D366' :
                              item.id === 'chat' ? '#2196F3' :
                              item.id === 'ai' ? '#9C27B0' : '#f44336'
                }}>
                  {item.badge > 99 ? '99+' : item.badge}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Main FAB Button */}
      <button
        style={{
          ...styles.fab,
          background: isOpen
            ? '#1a1a1a'
            : hasActiveCall
              ? '#4caf50'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)'
        }}
        onClick={toggleMenu}
        title={isOpen ? 'סגור' : 'תקשורת'}
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
        ) : hasActiveCall ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
            <circle cx="12" cy="10" r="1.5"/>
            <circle cx="8" cy="10" r="1.5"/>
            <circle cx="16" cy="10" r="1.5"/>
          </svg>
        )}

        {/* Total unread badge */}
        {!isOpen && totalUnread > 0 && (
          <div style={styles.totalBadge}>
            {totalUnread > 99 ? '99+' : totalUnread}
          </div>
        )}

        {/* Active call pulse */}
        {hasActiveCall && !isOpen && (
          <div style={styles.pulseRing} />
        )}
      </button>

      {/* Styles injected once */}
      <style>{`
        @keyframes menuIn {
          from { opacity: 0; transform: translateY(20px) scale(0.9); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes menuOut {
          from { opacity: 1; transform: translateY(0) scale(1); }
          to { opacity: 0; transform: translateY(20px) scale(0.9); }
        }
        @keyframes itemIn {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes itemOut {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(20px); }
        }
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(2); opacity: 0; }
        }
        @keyframes fabPulse {
          0%, 100% { box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4); }
          50% { box-shadow: 0 4px 30px rgba(102, 126, 234, 0.6); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 99990,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '12px'
  },
  fab: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative'
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '8px'
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 16px 8px 8px',
    background: 'white',
    border: 'none',
    borderRadius: '28px',
    cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
    transition: 'all 0.2s ease',
    direction: 'rtl',
    minWidth: '160px',
    position: 'relative'
  },
  menuItemIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    flexShrink: 0,
    position: 'relative'
  },
  menuItemLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#1a1a1a',
    whiteSpace: 'nowrap',
    flex: 1,
    textAlign: 'right'
  },
  badge: {
    minWidth: '20px',
    height: '20px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: '600',
    color: 'white',
    padding: '0 6px',
    marginLeft: '4px'
  },
  statusDot: {
    position: 'absolute',
    bottom: '2px',
    right: '2px',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    border: '2px solid white'
  },
  totalBadge: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    minWidth: '22px',
    height: '22px',
    borderRadius: '11px',
    background: '#f44336',
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
    border: '2px solid white'
  },
  pulseRing: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    border: '3px solid #4caf50',
    animation: 'pulse 1.5s infinite'
  }
};

export default UnifiedFloatingHub;
